import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import localForage from "localforage";
import CollaborationShell, { CollaborationShellTab } from "./CollaborationShell";
import PatientPicker from "../../../components/PatientPicker";
import { buildApiUrl, getAuthToken, getJsonAuthHeaders, useBaseUrl } from "../../../utils/utils";

interface CollaborationItem {
    collaboration_id: string;
    title: string;
    description: string;
    collaboration_type: string;
    status: string;
    patient: {
        id: string;
        name: string;
    };
    start_date: string;
    end_date: string;
    progress_percentage: number;
    billing: {
        total_amount: number;
        currency: string;
        payment_status: string;
    };
    stats: {
        tasks_count: number;
        completed_tasks: number;
        documents_count: number;
        telehealth_sessions: number;
    };
}

interface RequestItem {
    id: number;
    title: string;
    description: string;
    status: string;
    priority: string;
    engagement_type: string;
    patient_name?: string | null;
    created_at: string;
    start_date?: string | null;
    end_date?: string | null;
    request_type: "open" | "direct";
    creator: {
        id?: number | null;
        name?: string | null;
        clinic_name?: string | null;
    };
    required_services: string[];
    applications_count: number;
    min_pay?: number | null;
    max_pay?: number | null;
    response_time: number;
    direct_partner_names?: string[];
    qualificationMatches?: string[];
    qualificationScore?: number;
}

interface ReceivedApplicationItem {
    id: number;
    service_request: {
        id: number;
        title: string;
        description: string;
        status: string;
        priority: string;
        engagement_type: string;
        patient_name?: string | null;
        start_date?: string | null;
        end_date?: string | null;
        max_pay?: number | null;
    };
    applicant: {
        id: number;
        name: string;
        clinic_name: string;
        clinic_id: number;
    };
    status: string;
    application_date: string;
    proposed_rate?: number | null;
}

interface NegotiationItem {
    id: number;
    service_request: {
        id: number;
        title: string;
    };
    application: {
        applicant: {
            name: string;
            clinic_name: string;
        };
    };
    negotiation_status: string;
    last_updated: string;
    proposed_rate?: number | null;
    message: string;
}

interface BillingItem {
    billing_id: string;
    collaboration_id: string;
    collaboration_title: string;
    patient_name: string;
    total_amount: number;
    outstanding_amount: number;
    currency: string;
    payment_status: string;
    invoice_number?: string | null;
    updated_at: string;
    tasks_count: number;
    completed_tasks_count: number;
}

interface BillingSummary {
    total_amount: number;
    pending_amount: number;
    completed_amount: number;
}

interface ReportItem {
    report_id: string;
    title: string;
    content_preview: string;
    generated_by: string;
    generated_at: string;
    is_signed: boolean;
    task: {
        title: string;
        task_type: string;
        amount: number;
        currency: string;
    };
    collaboration: {
        collaboration_id: string;
        title: string;
        patient_name: string;
    };
}

interface ReportsSummary {
    total_reports: number;
    signed_reports: number;
    unsigned_reports: number;
}

interface ClinicService {
    service_id: number;
    service_name: string;
    service_title?: string;
    service_category?: string;
}

interface ClinicOption {
    id: number;
    name: string;
    email?: string;
    phone?: string;
}

interface RequestFormState {
    title: string;
    description: string;
    request_type: "open" | "direct";
    priority: "low" | "normal" | "high";
    engagement_type: string;
    patient_id: string;
    required_services: number[];
    direct_partners: number[];
    start_date: string;
    end_date: string;
    response_time: string;
    pay_mode: string;
    min_pay: string;
    max_pay: string;
    patient_consent_given: boolean;
    patient_consent_notes: string;
    task_expectations: string;
    preliminary_tasks: string;
}

type RequestComposerStep = 1 | 2 | 3 | 4;
type ApplicationComposerStep = 1 | 2 | 3;

interface ApplicationFormState {
    cover_letter: string;
    proposed_min_rate: string;
    proposed_max_rate: string;
    pay_mode: string;
    patient_flow: string;
    patient_flow_rate: string;
    qualifications: string;
    experience_years: string;
    certifications: string;
    equipment: string;
    info_request: string;
    task_acknowledgment: boolean;
}

const defaultRequestForm: RequestFormState = {
    title: "",
    description: "",
    request_type: "open",
    priority: "normal",
    engagement_type: "consultation",
    patient_id: "",
    required_services: [],
    direct_partners: [],
    start_date: "",
    end_date: "",
    response_time: "48",
    pay_mode: "per_request",
    min_pay: "",
    max_pay: "",
    patient_consent_given: true,
    patient_consent_notes: "",
    task_expectations: "",
    preliminary_tasks: "",
};

const defaultApplicationForm: ApplicationFormState = {
    cover_letter: "",
    proposed_min_rate: "",
    proposed_max_rate: "",
    pay_mode: "request",
    patient_flow: "",
    patient_flow_rate: "daily",
    qualifications: "",
    experience_years: "",
    certifications: "",
    equipment: "",
    info_request: "",
    task_acknowledgment: false,
};

const NormalizeText = (value?: string | null) => (value || "").trim().toLowerCase();
const FormatDate = (value?: string | null) => value ? new Date(value).toLocaleString() : "Not set";
const FormatMoney = (currency: string, value?: number | null) => `${currency || "KES"} ${Number(value || 0).toLocaleString()}`;
const ToIsoDate = (value: string, fallbackHour = "09:00") => value ? `${value}T${fallbackHour}:00` : undefined;
const CanRevealPatient = (status?: string) => ["active", "completed", "agreed", "patient_approved", "upgraded"].includes(NormalizeText(status));
const MaskPatientName = (name?: string | null, status?: string) => {
    if (!name) return "Patient (Protected)";
    if (CanRevealPatient(status)) return name;
    return name.split(/\s+/).map((part) => `${part.charAt(0)}${"*".repeat(Math.max(part.length - 1, 2))}`).join(" ");
};

const CollaborationCareWorkspacePage: React.FC = () => {
    const baseUrl = useBaseUrl();
    const location = useLocation();
    const navigate = useNavigate();
    const [clinicId, setClinicId] = useState("");
    const [clinicName, setClinicName] = useState("");
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [creatingRequest, setCreatingRequest] = useState(false);
    const [submittingApplication, setSubmittingApplication] = useState(false);
    const [search, setSearch] = useState("");
    const [displayMode, setDisplayMode] = useState<"grid" | "list">("grid");
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showApplicationModal, setShowApplicationModal] = useState(false);
    const [requestComposerStep, setRequestComposerStep] = useState<RequestComposerStep>(1);
    const [applicationComposerStep, setApplicationComposerStep] = useState<ApplicationComposerStep>(1);
    const [requestForm, setRequestForm] = useState<RequestFormState>(defaultRequestForm);
    const [applicationForm, setApplicationForm] = useState<ApplicationFormState>(defaultApplicationForm);
    const [applyingToRequest, setApplyingToRequest] = useState<RequestItem | null>(null);
    const [collaborations, setCollaborations] = useState<CollaborationItem[]>([]);
    const [openRequests, setOpenRequests] = useState<RequestItem[]>([]);
    const [openRequestsPage, setOpenRequestsPage] = useState<number>(1);
    const [openRequestsFilter, setOpenRequestsFilter] = useState<"all" | "active" | "expired">("all");
    const [openRequestsPagination, setOpenRequestsPagination] = useState({ current_page: 1, total_pages: 1, total_items: 0, has_next: false, has_previous: false });
    const [directRequests, setDirectRequests] = useState<RequestItem[]>([]);
    const [receivedApplications, setReceivedApplications] = useState<ReceivedApplicationItem[]>([]);
    const [negotiations, setNegotiations] = useState<NegotiationItem[]>([]);
    const [billings, setBillings] = useState<BillingItem[]>([]);
    const [billingSummary, setBillingSummary] = useState<BillingSummary | null>(null);
    const [reports, setReports] = useState<ReportItem[]>([]);
    const [reportsSummary, setReportsSummary] = useState<ReportsSummary | null>(null);
    const [clinicServices, setClinicServices] = useState<ClinicService[]>([]);
    const [partnerClinics, setPartnerClinics] = useState<ClinicOption[]>([]);
    const [selectedCollaborationId, setSelectedCollaborationId] = useState("");

    const activeTab = useMemo<CollaborationShellTab>(() => {
        if (location.pathname.endsWith("/find-requests")) return "find-requests";
        if (location.pathname.endsWith("/partnership-requests")) return "partnership-requests";
        if (location.pathname.endsWith("/received-requests")) return "received-requests";
        if (location.pathname.endsWith("/accounting")) return "accounting";
        if (location.pathname.endsWith("/reports")) return "reports";
        return "overview";
    }, [location.pathname]);

    const pageCopy = useMemo(() => {
        if (activeTab === "find-requests") {
            return {
                title: "Opportunity Feed",
                description: "Find care requests your facility can support, from cross-border patient continuity to capability-gap service delivery.",
            };
        }
        if (activeTab === "partnership-requests") {
            return {
                title: "Partner Outreach",
                description: "Review targeted requests and structured partnership opportunities that can evolve into long-running care collaborations.",
            };
        }
        if (activeTab === "received-requests") {
            return {
                title: "Agreements & Activation",
                description: "Move requests from partner review to patient approval and then into active collaboration workspaces.",
            };
        }
        if (activeTab === "accounting") {
            return {
                title: "Finance Workspace",
                description: "Track live collaboration value, outstanding balances, and the financial health of care-delivery partnerships.",
            };
        }
        if (activeTab === "reports") {
            return {
                title: "Evidence & Reports",
                description: "Keep reports, supporting evidence, and delivery outputs tied to the collaboration lifecycle.",
            };
        }
        return {
            title: "Collaboration Workspace",
            description: "Turn accepted requests into active cross-facility care workspaces where partners coordinate treatment, share updates, and deliver over the full service period.",
        };
    }, [activeTab]);

    const selectedCollaboration = useMemo(() => {
        return collaborations.find((item) => item.collaboration_id === selectedCollaborationId) || collaborations[0] || null;
    }, [collaborations, selectedCollaborationId]);

    const qualifiedOpenRequests = useMemo(() => {
        const serviceNames = new Set(
            clinicServices.flatMap((item) => [item.service_name, item.service_title].map((entry) => NormalizeText(entry)).filter(Boolean))
        );
        return openRequests
            .map((item) => {
                const matches = (item.required_services || []).filter((serviceName) => serviceNames.has(NormalizeText(serviceName)));
                return {
                    ...item,
                    qualificationMatches: matches,
                    qualificationScore: item.required_services.length ? matches.length / item.required_services.length : 1,
                };
            })
            .sort((left, right) => (right.qualificationScore || 0) - (left.qualificationScore || 0));
    }, [clinicServices, openRequests]);

    const visibleCollaborations = useMemo(() => collaborations.filter((item) => [item.title, item.description, item.patient.name, item.status].some((entry) => NormalizeText(entry).includes(NormalizeText(search)))), [collaborations, search]);
    const visibleOpenRequests = useMemo(() => qualifiedOpenRequests.filter((item) => [item.title, item.description, item.creator.name, item.patient_name, ...(item.required_services || [])].some((entry) => NormalizeText(entry).includes(NormalizeText(search)))), [qualifiedOpenRequests, search]);
    const visibleDirectRequests = useMemo(() => directRequests.filter((item) => [item.title, item.description, item.creator.name, item.patient_name, ...(item.direct_partner_names || [])].some((entry) => NormalizeText(entry).includes(NormalizeText(search)))), [directRequests, search]);
    const visibleApplications = useMemo(() => receivedApplications.filter((item) => [item.service_request.title, item.applicant.name, item.applicant.clinic_name, item.service_request.patient_name, item.status].some((entry) => NormalizeText(entry).includes(NormalizeText(search)))), [receivedApplications, search]);
    const visibleBillings = useMemo(() => billings.filter((item) => [item.collaboration_title, item.patient_name, item.payment_status, item.invoice_number].some((entry) => NormalizeText(entry).includes(NormalizeText(search)))), [billings, search]);
    const visibleReports = useMemo(() => reports.filter((item) => [item.title, item.collaboration.title, item.collaboration.patient_name, item.task.title, item.task.task_type].some((entry) => NormalizeText(entry).includes(NormalizeText(search)))), [reports, search]);

    const activationQueue = useMemo(() => visibleApplications.filter((item) => ["agreed", "patient_approved"].includes(NormalizeText(item.status)) || NormalizeText(item.service_request.status) === "agreed"), [visibleApplications]);
    const patientDecisionQueue = useMemo(() => visibleApplications.filter((item) => NormalizeText(item.service_request.status) === "patient_approval" && NormalizeText(item.status) === "accepted"), [visibleApplications]);
    const reviewQueue = useMemo(() => visibleApplications.filter((item) => ["submitted", "under_review", "negotiating"].includes(NormalizeText(item.status))), [visibleApplications]);

    const summaryCards = [
        { label: "Active care workspaces", value: visibleCollaborations.length, icon: "bx bx-network-chart", tone: "teal" },
        { label: "Ready to activate", value: activationQueue.length, icon: "bx bx-rocket", tone: "amber" },
        { label: "Awaiting patient decision", value: patientDecisionQueue.length, icon: "bx bx-user-check", tone: "blue" },
        { label: "Open partner opportunities", value: visibleOpenRequests.length + visibleDirectRequests.length, icon: "bx bx-globe", tone: "violet" },
    ];

    const ReadClinicIdentity = async () => {
        const storedClinicId = await localForage.getItem("clinic_id");
        const loginInfo: any = await localForage.getItem("uzima_login_info");
        const resolvedClinicId = String(storedClinicId || loginInfo?.clinic_id || "");
        setClinicId(resolvedClinicId);
        setClinicName(String(loginInfo?.clinic_name || localStorage.getItem("clinic_name") || "Your Clinic"));
        return resolvedClinicId;
    };

    const LoadCollaborations = async (resolvedClinicId: string) => {
        const headers = await getJsonAuthHeaders();
        const response = await fetch(buildApiUrl(baseUrl, `collaborations/collaborations/?clinic_id=${resolvedClinicId}`), { headers });
        const data = await response.json();
        setCollaborations(Array.isArray(data?.data) ? data.data : []);
    };

    const LoadOpenRequests = async (resolvedClinicId: string, page: number = 1, filter: string = "all") => {
        const headers = await getJsonAuthHeaders();
        const serviceIds = clinicServices.map((service) => service.service_id).filter(Boolean).join(",");
        const query = new URLSearchParams({
            clinic_id: resolvedClinicId,
            exclude_creator: "true",
            page: String(page),
            page_size: "12",
        });
        if (serviceIds) {
            query.set("service_ids", serviceIds);
        }
        if (filter === "active") {
            query.set("status", "active");
        } else if (filter === "expired") {
            query.set("status", "expired");
        }
        const response = await fetch(buildApiUrl(baseUrl, `collaborations/general-requests/?${query.toString()}`), { headers });
        const data = await response.json();
        setOpenRequests(Array.isArray(data?.data?.requests) ? data.data.requests : []);
        if (data?.data?.pagination) {
            setOpenRequestsPagination(data.data.pagination);
        }
    };

    const LoadDirectRequests = async (resolvedClinicId: string) => {
        const headers = await getJsonAuthHeaders();
        const response = await fetch(buildApiUrl(baseUrl, `collaborations/private-requests/?clinic_id=${resolvedClinicId}&page=1&page_size=100`), { headers });
        const data = await response.json();
        setDirectRequests(Array.isArray(data?.data?.requests) ? data.data.requests : []);
    };

    const LoadReceivedApplications = async (resolvedClinicId: string) => {
        const headers = await getJsonAuthHeaders();
        const response = await fetch(buildApiUrl(baseUrl, `collaborations/applications/received/?clinic_id=${resolvedClinicId}&page=1&page_size=100`), { headers });
        const data = await response.json();
        setReceivedApplications(Array.isArray(data?.data?.applications) ? data.data.applications : []);
    };

    const LoadNegotiations = async (resolvedClinicId: string) => {
        const headers = await getJsonAuthHeaders();
        const response = await fetch(buildApiUrl(baseUrl, `collaborations/negotiations/?clinic_id=${resolvedClinicId}&page=1&page_size=100`), { headers });
        const data = await response.json();
        setNegotiations(Array.isArray(data?.data?.negotiations) ? data.data.negotiations : []);
    };

    const LoadBillings = async (resolvedClinicId: string) => {
        const headers = await getJsonAuthHeaders();
        const response = await fetch(buildApiUrl(baseUrl, `collaborations/collaboration-billing/?clinic_id=${resolvedClinicId}`), { headers });
        const data = await response.json();
        setBillings(Array.isArray(data?.billings) ? data.billings : Array.isArray(data?.data?.billings) ? data.data.billings : []);
        setBillingSummary(data?.summary || data?.data?.summary || null);
    };

    const LoadReports = async (resolvedClinicId: string) => {
        const headers = await getJsonAuthHeaders();
        const response = await fetch(buildApiUrl(baseUrl, `collaborations/collaboration-reports/?clinic_id=${resolvedClinicId}`), { headers });
        const data = await response.json();
        setReports(Array.isArray(data?.reports) ? data.reports : Array.isArray(data?.data?.reports) ? data.data.reports : []);
        setReportsSummary(data?.summary || data?.data?.summary || null);
    };

    const LoadClinicServices = async (resolvedClinicId: string) => {
        const headers = await getJsonAuthHeaders();
        const response = await fetch(buildApiUrl(baseUrl, `facilities/services/clinic-services/${resolvedClinicId}/`), { headers });
        const data = await response.json();
        setClinicServices(Array.isArray(data?.data?.services) ? data.data.services : []);
    };

    const LoadPartnerClinics = async () => {
        const headers = await getJsonAuthHeaders();
        const response = await fetch(buildApiUrl(baseUrl, "facilities/clinics/all"), { headers });
        const data = await response.json();
        setPartnerClinics(Array.isArray(data?.data) ? data.data : []);
    };

    const RefreshWorkspace = async (resolvedClinicId = clinicId, initial = false) => {
        if (!resolvedClinicId) return;
        if (!initial) setRefreshing(true);
        await Promise.allSettled([
            LoadCollaborations(resolvedClinicId),
            LoadClinicServices(resolvedClinicId),
            LoadPartnerClinics(),
            LoadOpenRequests(resolvedClinicId, openRequestsPage, openRequestsFilter),
            LoadDirectRequests(resolvedClinicId),
            LoadReceivedApplications(resolvedClinicId),
            LoadNegotiations(resolvedClinicId),
            LoadBillings(resolvedClinicId),
            LoadReports(resolvedClinicId),
        ]);
        setRefreshing(false);
    };

    const InitializeWorkspace = async () => {
        try {
            setLoading(true);
            const resolvedClinicId = await ReadClinicIdentity();
            if (resolvedClinicId) {
                await Promise.all([
                    LoadClinicServices(resolvedClinicId),
                    LoadCollaborations(resolvedClinicId),
                    LoadDirectRequests(resolvedClinicId),
                    LoadReceivedApplications(resolvedClinicId),
                    LoadNegotiations(resolvedClinicId),
                    LoadBillings(resolvedClinicId),
                    LoadReports(resolvedClinicId),
                    LoadOpenRequests(resolvedClinicId, openRequestsPage, openRequestsFilter),
                ]);
            } else {
                Swal.fire({ icon: "warning", title: "Clinic not found", text: "Please sign in again so the collaboration workspace can load." });
                return;
            }
        } catch {
            Swal.fire({ icon: "error", title: "Initialization failed", text: "Unable to load collaboration workspace data." });
        } finally {
            setLoading(false);
        }
    };

    const OpenRequestComposer = () => {
        setRequestForm(defaultRequestForm);
        setRequestComposerStep(1);
        setShowCreateModal(true);
    };

    const CloseRequestComposer = () => {
        setShowCreateModal(false);
        setRequestComposerStep(1);
    };

    const CloseApplicationComposer = () => {
        setShowApplicationModal(false);
        setApplicationComposerStep(1);
        setApplyingToRequest(null);
    };

    const UpdateRequestField = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = event.target;
        setRequestForm((current) => ({
            ...current,
            [name]: type === "checkbox" ? (event.target as HTMLInputElement).checked : value,
        }));
    };

    const ToggleRequestSelection = (field: "required_services" | "direct_partners", value: number) => {
        setRequestForm((current) => ({
            ...current,
            [field]: current[field].includes(value) ? current[field].filter((item) => item !== value) : [...current[field], value],
        }));
    };

    const ValidateRequestComposerStep = (step: RequestComposerStep) => {
        if (step === 1) {
            if (!requestForm.title.trim() || !requestForm.description.trim() || !requestForm.patient_id) {
                Swal.fire({ icon: "warning", title: "Complete the request brief", text: "Add the title, description, and patient before continuing." });
                return false;
            }
        }
        if (step === 2) {
            if (!requestForm.start_date || !requestForm.response_time) {
                Swal.fire({ icon: "warning", title: "Timing is required", text: "Add the start date and response time to continue." });
                return false;
            }
            if (!requestForm.pay_mode) {
                Swal.fire({ icon: "warning", title: "Commercial details needed", text: "Choose the pay mode before continuing." });
                return false;
            }
        }
        if (step === 3) {
            if (requestForm.required_services.length === 0) {
                Swal.fire({ icon: "warning", title: "Services required", text: "Select at least one required service." });
                return false;
            }
            if (requestForm.request_type === "direct" && requestForm.direct_partners.length === 0) {
                Swal.fire({ icon: "warning", title: "Partner required", text: "Choose at least one target partner clinic for a direct request." });
                return false;
            }
        }
        return true;
    };

    const AdvanceRequestComposerStep = () => {
        if (!ValidateRequestComposerStep(requestComposerStep)) {
            return;
        }
        setRequestComposerStep((current) => Math.min(4, current + 1) as RequestComposerStep);
    };

    const RetreatRequestComposerStep = () => {
        setRequestComposerStep((current) => Math.max(1, current - 1) as RequestComposerStep);
    };

    const CreateRequest = async (publishNow: boolean) => {
        if (!clinicId) return;
        if (!requestForm.title || !requestForm.description || !requestForm.patient_id || !requestForm.start_date) {
            Swal.fire({ icon: "warning", title: "Missing details", text: "Complete the title, patient, description, and start date." });
            return;
        }
        if (requestForm.required_services.length === 0) {
            Swal.fire({ icon: "warning", title: "Services required", text: "Select at least one required service." });
            return;
        }
        if (requestForm.request_type === "direct" && requestForm.direct_partners.length === 0) {
            Swal.fire({ icon: "warning", title: "Partner required", text: "Choose at least one target partner clinic for a direct request." });
            return;
        }
        if (publishNow && !requestForm.patient_consent_given) {
            Swal.fire({ icon: "warning", title: "Patient consent needed", text: "You can only publish after patient consent is recorded." });
            return;
        }
        try {
            setCreatingRequest(true);
            const headers = await getJsonAuthHeaders();
            const response = await fetch(buildApiUrl(baseUrl, "collaborations/create-comprehensive-request/"), {
                method: "POST",
                headers,
                body: JSON.stringify({
                    title: requestForm.title,
                    description: requestForm.description,
                    engagement_type: requestForm.engagement_type,
                    creator_id: clinicId,
                    request_type: requestForm.request_type,
                    patient_id: requestForm.patient_id,
                    start_date: ToIsoDate(requestForm.start_date),
                    end_date: requestForm.end_date ? ToIsoDate(requestForm.end_date, "17:00") : null,
                    response_time: Number(requestForm.response_time || 48),
                    priority: requestForm.priority,
                    pay_mode: requestForm.pay_mode,
                    min_pay: requestForm.min_pay ? Number(requestForm.min_pay) : null,
                    max_pay: requestForm.max_pay ? Number(requestForm.max_pay) : null,
                    patient_consent_given: requestForm.patient_consent_given,
                    patient_consent_notes: requestForm.patient_consent_notes,
                    status: publishNow ? "pending" : "draft",
                    required_services: requestForm.required_services,
                    task_expectations: requestForm.task_expectations,
                    preliminary_tasks: requestForm.preliminary_tasks,
                    direct_partners: requestForm.request_type === "direct" ? requestForm.direct_partners : [],
                    share_patient_demographics: true,
                    share_medical_history: true,
                    share_current_medications: true,
                    share_lab_results: true,
                    share_imaging: true,
                    share_notes: true,
                }),
            });
            const data = await response.json();
            if (data?.status !== "success") {
                throw new Error(data?.message || "Unable to create request");
            }
            CloseRequestComposer();
            await RefreshWorkspace();
            Swal.fire({ icon: "success", title: publishNow ? "Request published" : "Draft saved", text: publishNow ? "The request is now live for partner engagement." : "The request draft has been saved.", timer: 1800, showConfirmButton: false });
        } catch (error: any) {
            Swal.fire({ icon: "error", title: "Creation failed", text: error?.message || "Unable to create the request." });
        } finally {
            setCreatingRequest(false);
        }
    };

    const requestComposerSteps: Array<{ step: RequestComposerStep; label: string }> = [
        { step: 1, label: "Request brief" },
        { step: 2, label: "Timeline & budget" },
        { step: 3, label: "Services & partners" },
        { step: 4, label: "Consent & publish" },
    ];

    const renderRequestComposerBody = () => {
        if (requestComposerStep === 1) {
            return (
                <>
                    <div className="cw-summary-card">
                        <strong>{requestForm.request_type === "open" ? "Public marketplace request" : "Targeted partnership request"}</strong>
                        <p>{requestForm.request_type === "open" ? "Open requests are visible across partner clinics so anyone qualified can respond." : "Direct requests are only shown to the specific partner clinics you choose."}</p>
                    </div>
                    <div className="sc-form-row">
                        <div className="sc-form-group"><label>Title</label><input className="sc-input" name="title" value={requestForm.title} onChange={UpdateRequestField} /></div>
                        <div className="sc-form-group"><label>Request type</label><select className="sc-input" name="request_type" value={requestForm.request_type} onChange={UpdateRequestField}><option value="open">Open request</option><option value="direct">Direct partnership request</option></select></div>
                    </div>
                    <div className="sc-form-group"><label>Description</label><textarea className="sc-textarea" rows={5} name="description" value={requestForm.description} onChange={UpdateRequestField} /></div>
                    <div className="sc-form-row">
                        <div className="sc-form-group"><label>Patient</label><PatientPicker baseUrl={baseUrl} clinicId={clinicId} value={requestForm.patient_id} onChange={(patientId) => setRequestForm((current) => ({ ...current, patient_id: patientId }))} /></div>
                        <div className="sc-form-group"><label>Priority</label><select className="sc-input" name="priority" value={requestForm.priority} onChange={UpdateRequestField}><option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option></select></div>
                    </div>
                    <div className="sc-form-group"><label>Engagement type</label><select className="sc-input" name="engagement_type" value={requestForm.engagement_type} onChange={UpdateRequestField}><option value="consultation">Consultation</option><option value="term">Term</option><option value="project">Project</option><option value="routine">Routine</option></select></div>
                </>
            );
        }

        if (requestComposerStep === 2) {
            return (
                <>
                    <div className="sc-form-row">
                        <div className="sc-form-group"><label>Start date</label><input type="date" className="sc-input" name="start_date" value={requestForm.start_date} onChange={UpdateRequestField} /></div>
                        <div className="sc-form-group"><label>End date</label><input type="date" className="sc-input" name="end_date" value={requestForm.end_date} onChange={UpdateRequestField} /></div>
                    </div>
                    <div className="sc-form-row">
                        <div className="sc-form-group"><label>Response time (hours)</label><input type="number" min="1" className="sc-input" name="response_time" value={requestForm.response_time} onChange={UpdateRequestField} /></div>
                        <div className="sc-form-group"><label>Pay mode</label><select className="sc-input" name="pay_mode" value={requestForm.pay_mode} onChange={UpdateRequestField}><option value="per_request">Per request</option><option value="per_person">Per person</option><option value="per_milestone">Per milestone</option></select></div>
                    </div>
                    <div className="sc-form-row">
                        <div className="sc-form-group"><label>Minimum pay</label><input type="number" min="0" className="sc-input" name="min_pay" value={requestForm.min_pay} onChange={UpdateRequestField} /></div>
                        <div className="sc-form-group"><label>Maximum pay</label><input type="number" min="0" className="sc-input" name="max_pay" value={requestForm.max_pay} onChange={UpdateRequestField} /></div>
                    </div>
                    <div className="sc-form-group"><label>Task expectations</label><textarea className="sc-textarea" rows={4} name="task_expectations" value={requestForm.task_expectations} onChange={UpdateRequestField} /></div>
                    <div className="sc-form-group"><label>Preliminary tasks</label><textarea className="sc-textarea" rows={4} name="preliminary_tasks" value={requestForm.preliminary_tasks} onChange={UpdateRequestField} /></div>
                </>
            );
        }

        if (requestComposerStep === 3) {
            return (
                <>
                    <div className="sc-form-group"><label>Required services</label><div className="cw-choice-grid">{clinicServices.map((service) => <label className="cw-choice-card" key={service.service_id}><input type="checkbox" checked={requestForm.required_services.includes(service.service_id)} onChange={() => ToggleRequestSelection("required_services", service.service_id)} /><span><strong>{service.service_title || service.service_name}</strong><small>{service.service_category || "General"}</small></span></label>)}</div></div>
                    {requestForm.request_type === "direct" ? <div className="sc-form-group"><label>Target partner clinics</label><div className="cw-choice-grid">{partnerClinics.filter((item) => String(item.id) !== clinicId).map((clinic) => <label className="cw-choice-card" key={clinic.id}><input type="checkbox" checked={requestForm.direct_partners.includes(clinic.id)} onChange={() => ToggleRequestSelection("direct_partners", clinic.id)} /><span><strong>{clinic.name}</strong><small>{clinic.email || clinic.phone || "Partner clinic"}</small></span></label>)}</div></div> : <div className="cw-summary-card"><strong>Public request visibility</strong><p>This open request will be visible to other partner clinics in the opportunity feed.</p></div>}
                </>
            );
        }

        return (
            <>
                <div className="sc-form-row">
                    <div className="sc-form-group"><label>Patient consent recorded</label><label className="cw-switch-row"><input type="checkbox" name="patient_consent_given" checked={requestForm.patient_consent_given} onChange={UpdateRequestField} /><span>Consent available for circulation</span></label></div>
                    <div className="sc-form-group"><label>Patient consent notes</label><input className="sc-input" name="patient_consent_notes" value={requestForm.patient_consent_notes} onChange={UpdateRequestField} /></div>
                </div>
                <div className="cw-summary-card">
                    <strong>Ready to publish</strong>
                    <p>{requestForm.request_type === "open" ? "Publishing will place this request in the shared partner marketplace." : "Publishing will send this request only to the selected partner clinics."}</p>
                    <div className="cw-metrics-row muted">
                        <span>{requestForm.required_services.length} services selected</span>
                        <span>{requestForm.request_type === "direct" ? `${requestForm.direct_partners.length} direct partners` : "Public visibility enabled"}</span>
                        <span>{requestForm.start_date || "No start date"}</span>
                    </div>
                </div>
            </>
        );
    };

    const OpenApplicationComposer = (request: RequestItem) => {
        setApplyingToRequest(request);
        setApplicationForm(defaultApplicationForm);
        setApplicationComposerStep(1);
        setShowApplicationModal(true);
    };

    const ValidateApplicationComposerStep = (step: ApplicationComposerStep) => {
        if (step === 1) {
            if (!applicationForm.qualifications.trim()) {
                Swal.fire({ icon: "warning", title: "Qualifications required", text: "Describe your clinic's qualifications before continuing." });
                return false;
            }
        }
        if (step === 2) {
            if (!applicationForm.proposed_min_rate || !applicationForm.proposed_max_rate) {
                Swal.fire({ icon: "warning", title: "Rate proposal required", text: "Provide your proposed minimum and maximum rates." });
                return false;
            }
        }
        if (step === 3) {
            if (!applicationForm.task_acknowledgment) {
                Swal.fire({ icon: "warning", title: "Acknowledgment required", text: "Confirm your team can deliver the requested scope of work." });
                return false;
            }
        }
        return true;
    };

    const AdvanceApplicationComposerStep = () => {
        if (!ValidateApplicationComposerStep(applicationComposerStep)) return;
        setApplicationComposerStep((current) => Math.min(3, current + 1) as ApplicationComposerStep);
    };

    const RetreatApplicationComposerStep = () => {
        setApplicationComposerStep((current) => Math.max(1, current - 1) as ApplicationComposerStep);
    };

    const UpdateApplicationField = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = event.target;
        setApplicationForm((current) => ({
            ...current,
            [name]: type === "checkbox" ? (event.target as HTMLInputElement).checked : value,
        }));
    };

    const SubmitApplication = async () => {
        if (!applyingToRequest) return;
        if (!applicationForm.qualifications.trim()) {
            Swal.fire({ icon: "warning", title: "Qualifications required", text: "Describe your clinic's qualifications before submitting." });
            return;
        }
        if (!applicationForm.proposed_min_rate || !applicationForm.proposed_max_rate) {
            Swal.fire({ icon: "warning", title: "Rate proposal required", text: "Provide your proposed minimum and maximum rates." });
            return;
        }
        if (!applicationForm.task_acknowledgment) {
            Swal.fire({ icon: "warning", title: "Acknowledgment required", text: "Confirm your team can deliver the requested scope of work." });
            return;
        }
        try {
            setSubmittingApplication(true);
            const token = await getAuthToken();
            const formData = new FormData();
            formData.append("service_request", String(applyingToRequest.id));
            formData.append("partner_applying_id", clinicId);
            formData.append("application_note", applicationForm.cover_letter || "Application submitted through the collaboration workspace.");
            formData.append("partner_minimum", applicationForm.proposed_min_rate);
            formData.append("partner_maximum", applicationForm.proposed_max_rate);
            formData.append("pay_mode", applicationForm.pay_mode);
            formData.append("patient_flow", applicationForm.patient_flow || "1");
            formData.append("patient_flow_rate", applicationForm.patient_flow_rate);
            formData.append("partner_qualifications", applicationForm.qualifications);
            formData.append("partner_experience_years", applicationForm.experience_years || "0");
            formData.append("partner_certifications", applicationForm.certifications);
            formData.append("partner_equipment", applicationForm.equipment);
            if (applicationForm.info_request.trim()) formData.append("info_request", applicationForm.info_request);
            const response = await fetch(buildApiUrl(baseUrl, "collaborations/applications/"), {
                method: "POST",
                headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                body: formData,
            });
            const data = await response.json();
            if (data?.status !== "success") {
                throw new Error(data?.message || "Unable to submit application");
            }
            CloseApplicationComposer();
            await RefreshWorkspace();
            Swal.fire({ icon: "success", title: "Application submitted", text: "Your partner proposal is now in the structured review workflow.", timer: 2200, showConfirmButton: false });
        } catch (error: any) {
            Swal.fire({ icon: "error", title: "Submission failed", text: error?.message || "Unable to submit the application." });
        } finally {
            setSubmittingApplication(false);
        }
    };

    const SendNegotiationMessage = async (application: ReceivedApplicationItem) => {
        const result = await Swal.fire({
            title: `Negotiate with ${application.applicant.name}`,
            input: "textarea",
            inputLabel: "Negotiation message",
            inputPlaceholder: "Share your clarification, counter-offer, or operational update...",
            showCancelButton: true,
            confirmButtonText: "Send update",
        });
        if (!result.isConfirmed || !result.value) return;
        try {
            const headers = await getJsonAuthHeaders();
            const response = await fetch(buildApiUrl(baseUrl, `collaborations/applications/${application.id}/negotiate/`), {
                method: "POST",
                headers,
                body: JSON.stringify({
                    sender_id: Number(clinicId),
                    recipient_id: application.applicant.clinic_id,
                    message: result.value,
                    message_type: "counter_offer",
                    patient_visible: true,
                }),
            });
            const data = await response.json();
            if (data?.status !== "success") {
                throw new Error(data?.message || "Unable to send negotiation update");
            }
            await RefreshWorkspace();
            Swal.fire({ icon: "success", title: "Negotiation sent", text: "The partner has your latest terms and update." });
        } catch (error: any) {
            Swal.fire({ icon: "error", title: "Negotiation failed", text: error?.message || "Unable to send the negotiation update." });
        }
    };

    const SelectPartner = async (application: ReceivedApplicationItem) => {
        const result = await Swal.fire({
            title: `Select ${application.applicant.name}`,
            input: "number",
            inputLabel: "Final agreed rate",
            inputPlaceholder: String(application.proposed_rate || application.service_request.max_pay || 0),
            showCancelButton: true,
            confirmButtonText: "Select partner",
        });
        if (!result.isConfirmed) return;
        try {
            const headers = await getJsonAuthHeaders();
            const response = await fetch(buildApiUrl(baseUrl, `collaborations/applications/${application.id}/select/`), {
                method: "POST",
                headers,
                body: JSON.stringify({
                    final_rate: result.value ? Number(result.value) : application.proposed_rate || application.service_request.max_pay || 0,
                    final_pay_mode: "per_request",
                }),
            });
            const data = await response.json();
            if (data?.status !== "success") {
                throw new Error(data?.message || "Unable to select partner");
            }
            await RefreshWorkspace();
            Swal.fire({ icon: "success", title: "Partner selected", text: "This request is now ready for patient approval." });
        } catch (error: any) {
            Swal.fire({ icon: "error", title: "Selection failed", text: error?.message || "Unable to select the partner." });
        }
    };

    const RecordPatientDecision = async (application: ReceivedApplicationItem, approved: boolean) => {
        const result = await Swal.fire({
            title: approved ? "Approve partner" : "Reject partner",
            input: "textarea",
            inputLabel: "Patient notes",
            inputPlaceholder: approved ? "Add any conditions for the care period..." : "Add the reason for the rejection...",
            showCancelButton: true,
            confirmButtonText: approved ? "Approve" : "Reject",
            confirmButtonColor: approved ? "#0f766e" : "#dc3545",
        });
        if (!result.isConfirmed) return;
        try {
            const headers = await getJsonAuthHeaders();
            const response = await fetch(buildApiUrl(baseUrl, `collaborations/applications/${application.id}/patient-approval/`), {
                method: "POST",
                headers,
                body: JSON.stringify({
                    patient_approved: approved,
                    patient_id: 0,
                    patient_notes: result.value || "",
                }),
            });
            const data = await response.json();
            if (data?.status !== "success") {
                throw new Error(data?.message || "Unable to record patient decision");
            }
            await RefreshWorkspace();
            Swal.fire({ icon: approved ? "success" : "info", title: approved ? "Partner approved" : "Partner rejected", text: approved ? "The request has reached agreement and can now become an active collaboration." : "The request is back in review for other partners." });
        } catch (error: any) {
            Swal.fire({ icon: "error", title: "Decision failed", text: error?.message || "Unable to record the patient decision." });
        }
    };

    const ActivateCollaboration = async (serviceRequestId: number) => {
        const result = await Swal.fire({
            title: "Activate collaboration workspace",
            text: "This will convert the agreed request into a live collaboration workspace for ongoing delivery.",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Activate workspace",
            confirmButtonColor: "#0f766e",
        });
        if (!result.isConfirmed) return;
        try {
            const headers = await getJsonAuthHeaders();
            const response = await fetch(buildApiUrl(baseUrl, `collaborations/service-requests/${serviceRequestId}/upgrade/`), {
                method: "POST",
                headers,
            });
            const data = await response.json();
            if (data?.success === false || data?.status === "error") {
                throw new Error(data?.message || "Unable to activate the collaboration workspace");
            }
            await RefreshWorkspace();
            Swal.fire({ icon: "success", title: "Workspace activated", text: "The accepted request is now an active collaboration.", timer: 1800, showConfirmButton: false });
            navigate("/institution/collaborations");
        } catch (error: any) {
            Swal.fire({ icon: "error", title: "Activation failed", text: error?.message || "Unable to activate the collaboration workspace." });
        }
    };

    useEffect(() => {
        document.title = "Uzima - Collaboration Workspace";
        InitializeWorkspace();
    }, []);

    useEffect(() => {
        if (!selectedCollaborationId && collaborations.length > 0) {
            setSelectedCollaborationId(collaborations[0].collaboration_id);
        }
    }, [collaborations, selectedCollaborationId]);

    useEffect(() => {
        if (clinicId) {
            LoadOpenRequests(clinicId, openRequestsPage, openRequestsFilter);
        }
    }, [openRequestsPage, openRequestsFilter]);

    const renderOverview = () => (
        <>
            <div className="cw-hero sc-panel">
                <div className="cw-hero-body">
                    <div>
                        <div className="cw-eyebrow">Cross-facility care delivery</div>
                        <h2>Run accepted requests like real treatment workspaces</h2>
                        <p>Coordinate referrals, diagnostics, treatment handoffs, and long-running care periods across partner facilities without losing the patient journey.</p>
                    </div>
                    <div className="cw-hero-actions">
                        <button type="button" className={`sc-btn ${displayMode === "grid" ? "sc-btn-primary" : "sc-btn-outline"}`} onClick={() => setDisplayMode("grid")}>Grid view</button>
                        <button type="button" className={`sc-btn ${displayMode === "list" ? "sc-btn-primary" : "sc-btn-outline"}`} onClick={() => setDisplayMode("list")}>List view</button>
                    </div>
                </div>
            </div>

            <div className="sc-stats">
                {summaryCards.map((item) => (
                    <div className="sc-stat" key={item.label}>
                        <div className={`sc-stat-icon ${item.tone}`}><i className={item.icon}></i></div>
                        <div className="sc-stat-value">{item.value}</div>
                        <div className="sc-stat-label">{item.label}</div>
                    </div>
                ))}
            </div>

            <div className="cw-overview-grid">
                <div>
                    <div className="sc-panel">
                        <div className="sc-panel-head">
                            <div>
                                <h3>Active collaboration workspaces</h3>
                                <p>Accepted requests that are already coordinating delivery, updates, documentation, and patient progress.</p>
                            </div>
                            <span className="cw-count-pill">{visibleCollaborations.length} active</span>
                        </div>
                        <div className="sc-panel-body">
                            {visibleCollaborations.length === 0 ? (
                                <div className="sc-empty">
                                    <i className="bx bx-network-chart"></i>
                                    <h3>No active collaboration workspaces yet</h3>
                                    <p>Move an agreed request through activation and it will appear here as a live workspace.</p>
                                </div>
                            ) : (
                                <div className={displayMode === "grid" ? "cw-collab-grid" : "cw-collab-list"}>
                                    {visibleCollaborations.map((item) => (
                                        <button type="button" key={item.collaboration_id} className={`cw-collab-card ${selectedCollaboration?.collaboration_id === item.collaboration_id ? "active" : ""}`} onClick={() => setSelectedCollaborationId(item.collaboration_id)}>
                                            <div className="cw-collab-head">
                                                <div>
                                                    <strong>{item.title}</strong>
                                                    <span>{MaskPatientName(item.patient.name, item.status)}</span>
                                                </div>
                                                <span className={`cw-status ${NormalizeText(item.status)}`}>{item.status.replace(/_/g, " ")}</span>
                                            </div>
                                            <p>{item.description}</p>
                                            <div className="cw-metrics-row">
                                                <span><i className="bx bx-task"></i>{item.stats.tasks_count} tasks</span>
                                                <span><i className="bx bx-file"></i>{item.stats.documents_count} reports</span>
                                                <span><i className="bx bx-video"></i>{item.stats.telehealth_sessions} sessions</span>
                                            </div>
                                            <div className="cw-progress-row">
                                                <div className="cw-progress-track"><div style={{ width: `${item.progress_percentage || 0}%` }}></div></div>
                                                <small>{item.progress_percentage || 0}% progress</small>
                                            </div>
                                            <div className="cw-metrics-row muted">
                                                <span>{FormatDate(item.start_date)}</span>
                                                <span>{FormatMoney(item.billing.currency, item.billing.total_amount)}</span>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div>
                    <div className="sc-panel cw-side-panel">
                        <div className="sc-panel-head">
                            <div>
                                <h3>Focused workspace</h3>
                                <p>Use one collaboration as the operational center for delivery, updates, and partner actions.</p>
                            </div>
                        </div>
                        <div className="sc-panel-body">
                            {selectedCollaboration ? (
                                <div className="cw-focus-card">
                                    <div className="cw-focus-top">
                                        <div>
                                            <h4>{selectedCollaboration.title}</h4>
                                            <span>{MaskPatientName(selectedCollaboration.patient.name, selectedCollaboration.status)}</span>
                                        </div>
                                        <span className={`cw-status ${NormalizeText(selectedCollaboration.status)}`}>{selectedCollaboration.status.replace(/_/g, " ")}</span>
                                    </div>
                                    <p>{selectedCollaboration.description}</p>
                                    <div className="cw-focus-meta">
                                        <div><span>Care window</span><strong>{FormatDate(selectedCollaboration.start_date)}</strong></div>
                                        <div><span>Projected value</span><strong>{FormatMoney(selectedCollaboration.billing.currency, selectedCollaboration.billing.total_amount)}</strong></div>
                                        <div><span>Completed tasks</span><strong>{selectedCollaboration.stats.completed_tasks}</strong></div>
                                        <div><span>Telehealth sessions</span><strong>{selectedCollaboration.stats.telehealth_sessions}</strong></div>
                                    </div>
                                    <div className="cw-action-row">
                                        <button type="button" className="sc-btn sc-btn-primary" onClick={() => navigate(`/institution/messaging?collaboration_id=${selectedCollaboration.collaboration_id}&context_type=collaboration`)}>Open collaboration messages</button>
                                        <button type="button" className="sc-btn sc-btn-outline" onClick={() => navigate("/institution/telehealth")}>Open telehealth</button>
                                        <button type="button" className="sc-btn sc-btn-ghost" onClick={() => navigate("/institution/collaborations/reports")}>Open reports</button>
                                    </div>
                                </div>
                            ) : (
                                <div className="sc-empty" style={{ padding: "32px 20px" }}>
                                    <i className="bx bx-select-multiple"></i>
                                    <h3>Select a workspace</h3>
                                    <p>Choose an active collaboration to focus the right-hand panel.</p>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="sc-panel cw-side-panel">
                        <div className="sc-panel-head">
                            <div>
                                <h3>Ready to activate</h3>
                                <p>Accepted requests that should now become live collaboration workspaces.</p>
                            </div>
                            <span className="cw-count-pill">{activationQueue.length}</span>
                        </div>
                        <div className="sc-panel-body">
                            {activationQueue.length === 0 ? (
                                <div className="sc-empty" style={{ padding: "28px 20px" }}>
                                    <i className="bx bx-rocket"></i>
                                    <h3>No activation queue</h3>
                                    <p>Once a patient-approved request reaches agreement, it will appear here.</p>
                                </div>
                            ) : (
                                <div className="sc-list-stack">
                                    {activationQueue.slice(0, 4).map((item) => (
                                        <div className="sc-list-item cw-queue-item" key={item.id}>
                                            <div>
                                                <strong>{item.service_request.title}</strong>
                                                <div>{item.applicant.clinic_name} · {MaskPatientName(item.service_request.patient_name, item.status)}</div>
                                            </div>
                                            <button type="button" className="sc-btn sc-btn-primary sc-btn-sm" onClick={() => ActivateCollaboration(item.service_request.id)}>Activate</button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="sc-panel cw-side-panel">
                        <div className="sc-panel-head">
                            <div>
                                <h3>Care delivery patterns</h3>
                                <p>Model the workspace around the real B2B scenarios you are coordinating.</p>
                            </div>
                        </div>
                        <div className="sc-panel-body">
                            <div className="sc-list-stack">
                                <div className="sc-list-item"><div><strong>Cross-border continuity</strong><div>Move a patient from one country to another without losing treatment coordination.</div></div></div>
                                <div className="sc-list-item"><div><strong>Capability-gap referrals</strong><div>Coordinate imaging, specialist services, or medicines your current facility cannot provide.</div></div></div>
                                <div className="sc-list-item"><div><strong>Long-running joint delivery</strong><div>Keep partners aligned across the full requested period, not just a one-off referral event.</div></div></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );

    const renderOpportunityFeed = () => (
        <div className="sc-panel">
            <div className="sc-panel-head">
                <div>
                    <h3>Open requests your facility can support</h3>
                    <p>Use qualification overlap to spot requests that can quickly become active delivery partnerships.</p>
                </div>
                <div className="cw-panel-actions" style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <select className="sc-input" style={{ minWidth: "160px", padding: "8px 12px", height: "38px" }} value={openRequestsFilter} onChange={(e) => { setOpenRequestsFilter(e.target.value as any); setOpenRequestsPage(1); }}>
                        <option value="all">All Opportunities</option>
                        <option value="active">Active Only</option>
                        <option value="expired">Expired Only</option>
                    </select>
                    <span className="cw-count-pill" style={{ margin: 0 }}>{openRequestsPagination.total_items} opportunities</span>
                </div>
            </div>
            <div className="sc-panel-body">
                {visibleOpenRequests.length === 0 ? (
                    <div className="sc-empty"><i className="bx bx-search-alt"></i><h3>No opportunities found</h3><p>Adjust your search, filters, or refresh the feed.</p></div>
                ) : (
                    <>
                        <div className="cw-request-grid">
                            {visibleOpenRequests.map((item) => {
                                const isExpired = item.end_date && new Date(item.end_date) < new Date();
                                const badgeClass = isExpired ? "danger" : "success";
                                const badgeLabel = isExpired ? "Expired" : "Active";
                                return (
                                    <div className="cw-request-card" key={item.id}>
                                        <div className="cw-request-head">
                                            <div>
                                                <strong>{item.title}</strong>
                                                <span>{item.creator.clinic_name || item.creator.name || "Partner facility"}</span>
                                            </div>
                                            <span className={`cw-status ${badgeClass}`}>{badgeLabel}</span>
                                        </div>
                                        <p>{item.description}</p>
                                        <div className="cw-chip-row">
                                            {(item.qualificationMatches || []).length > 0 ? (item.qualificationMatches || []).map((match) => <span className="cw-chip" key={match}>{match}</span>) : <span className="cw-chip muted">No exact overlap yet</span>}
                                        </div>
                                        <div className="cw-metrics-row muted">
                                            <span>{MaskPatientName(item.patient_name, item.status)}</span>
                                            <span>{FormatMoney("KES", item.max_pay || item.min_pay)}</span>
                                            <span>{item.applications_count} applications</span>
                                        </div>
                                        <div className="cw-card-actions">
                                            <button type="button" className="sc-btn sc-btn-primary sc-btn-sm" onClick={() => OpenApplicationComposer(item)} disabled={isExpired}>Apply</button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {openRequestsPagination.total_pages > 1 && (
                            <div className="cw-pagination">
                                <button type="button" className="sc-btn sc-btn-ghost sc-btn-sm" disabled={!openRequestsPagination.has_previous} onClick={() => setOpenRequestsPage(p => Math.max(1, p - 1))}>Previous</button>
                                <span>Page {openRequestsPagination.current_page} of {openRequestsPagination.total_pages}</span>
                                <button type="button" className="sc-btn sc-btn-ghost sc-btn-sm" disabled={!openRequestsPagination.has_next} onClick={() => setOpenRequestsPage(p => Math.min(openRequestsPagination.total_pages, p + 1))}>Next</button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );

    const renderPartnerOutreach = () => (
        <div className="sc-panel">
            <div className="sc-panel-head">
                <div>
                    <h3>Targeted partnership requests</h3>
                    <p>Direct requests for strategic or specialist partnerships that can evolve into continuous delivery workspaces.</p>
                </div>
                <span className="cw-count-pill">{visibleDirectRequests.length} targeted</span>
            </div>
            <div className="sc-panel-body">
                {visibleDirectRequests.length === 0 ? (
                    <div className="sc-empty"><i className="bx bx-building-house"></i><h3>No targeted requests right now</h3><p>New direct opportunities will appear here as facilities reach out.</p></div>
                ) : (
                    <div className="cw-request-grid">
                        {visibleDirectRequests.map((item) => (
                            <div className="cw-request-card" key={item.id}>
                                <div className="cw-request-head">
                                    <div>
                                        <strong>{item.title}</strong>
                                        <span>{item.creator.clinic_name || item.creator.name || "Partner facility"}</span>
                                    </div>
                                    <span className={`cw-status ${NormalizeText(item.status)}`}>{item.status.replace(/_/g, " ")}</span>
                                </div>
                                <p>{item.description}</p>
                                <div className="cw-metrics-row muted">
                                    <span>{MaskPatientName(item.patient_name, item.status)}</span>
                                    <span>{item.engagement_type.replace(/_/g, " ")}</span>
                                    <span>{FormatMoney("KES", item.max_pay || item.min_pay)}</span>
                                </div>
                                <div className="cw-chip-row">
                                    {(item.required_services || []).map((service) => <span className="cw-chip" key={service}>{service}</span>)}
                                </div>
                                <div className="cw-card-actions">
                                    <button type="button" className="sc-btn sc-btn-primary sc-btn-sm" onClick={() => OpenApplicationComposer(item)}>Respond</button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );

    const renderAgreements = () => (
        <div className="cw-agreements-grid">
            <div className="sc-panel">
                <div className="sc-panel-head">
                    <div>
                        <h3>Awaiting patient approval</h3>
                        <p>Once a partner is shortlisted, record the patient decision to move toward agreement.</p>
                    </div>
                    <span className="cw-count-pill">{patientDecisionQueue.length}</span>
                </div>
                <div className="sc-panel-body">
                    {patientDecisionQueue.length === 0 ? (
                        <div className="sc-empty" style={{ padding: "28px 20px" }}><i className="bx bx-user-check"></i><h3>No pending patient decisions</h3><p>Shortlisted partners awaiting patient approval will appear here.</p></div>
                    ) : (
                        <div className="cw-action-list">
                            {patientDecisionQueue.map((item) => (
                                <div className="cw-action-card" key={item.id}>
                                    <div>
                                        <strong>{item.service_request.title}</strong>
                                        <p>{item.applicant.clinic_name} · {MaskPatientName(item.service_request.patient_name, item.status)}</p>
                                    </div>
                                    <div className="cw-inline-actions">
                                        <button type="button" className="sc-btn sc-btn-primary sc-btn-sm" onClick={() => RecordPatientDecision(item, true)}>Approve</button>
                                        <button type="button" className="sc-btn sc-btn-danger sc-btn-sm" onClick={() => RecordPatientDecision(item, false)}>Reject</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="sc-panel">
                <div className="sc-panel-head">
                    <div>
                        <h3>Activation queue</h3>
                        <p>Agreement reached. Convert the request into a collaboration workspace and start delivery.</p>
                    </div>
                    <span className="cw-count-pill">{activationQueue.length}</span>
                </div>
                <div className="sc-panel-body">
                    {activationQueue.length === 0 ? (
                        <div className="sc-empty" style={{ padding: "28px 20px" }}><i className="bx bx-rocket"></i><h3>No agreements ready</h3><p>Agreed requests will appear here once they are ready to activate.</p></div>
                    ) : (
                        <div className="cw-action-list">
                            {activationQueue.map((item) => (
                                <div className="cw-action-card" key={item.id}>
                                    <div>
                                        <strong>{item.service_request.title}</strong>
                                        <p>{item.applicant.name} · {item.applicant.clinic_name}</p>
                                    </div>
                                    <button type="button" className="sc-btn sc-btn-primary sc-btn-sm" onClick={() => ActivateCollaboration(item.service_request.id)}>Activate workspace</button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="sc-panel cw-agreements-wide">
                <div className="sc-panel-head">
                    <div>
                        <h3>Partner review and negotiation</h3>
                        <p>Shortlist incoming applicants, negotiate terms, and move the right partner into approval.</p>
                    </div>
                    <span className="cw-count-pill">{reviewQueue.length} in review</span>
                </div>
                <div className="sc-panel-body">
                    {reviewQueue.length === 0 ? (
                        <div className="sc-empty" style={{ padding: "28px 20px" }}><i className="bx bx-message-square-dots"></i><h3>No live partner reviews</h3><p>Incoming applications and negotiations will appear here.</p></div>
                    ) : (
                        <div className="cw-action-list">
                            {reviewQueue.map((item) => (
                                <div className="cw-action-card" key={item.id}>
                                    <div>
                                        <strong>{item.service_request.title}</strong>
                                        <p>{item.applicant.name} · {item.applicant.clinic_name} · {FormatMoney("KES", item.proposed_rate || item.service_request.max_pay)}</p>
                                    </div>
                                    <div className="cw-inline-actions">
                                        <button type="button" className="sc-btn sc-btn-outline sc-btn-sm" onClick={() => SendNegotiationMessage(item)}>Negotiate</button>
                                        <button type="button" className="sc-btn sc-btn-primary sc-btn-sm" onClick={() => SelectPartner(item)}>Select partner</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    {negotiations.length > 0 ? (
                        <div className="cw-timeline">
                            {negotiations.slice(0, 6).map((item) => (
                                <div className="cw-timeline-item" key={item.id}>
                                    <div className="cw-timeline-dot"></div>
                                    <div>
                                        <strong>{item.service_request.title}</strong>
                                        <p>{item.application.applicant.clinic_name} · {item.message}</p>
                                        <small>{FormatDate(item.last_updated)}</small>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );

    const renderFinance = () => (
        <>
            <div className="sc-stats">
                <div className="sc-stat"><div className="sc-stat-icon teal"><i className="bx bx-wallet"></i></div><div className="sc-stat-value">{FormatMoney("KES", billingSummary?.total_amount)}</div><div className="sc-stat-label">Portfolio value</div></div>
                <div className="sc-stat"><div className="sc-stat-icon amber"><i className="bx bx-timer"></i></div><div className="sc-stat-value">{FormatMoney("KES", billingSummary?.pending_amount)}</div><div className="sc-stat-label">Outstanding</div></div>
                <div className="sc-stat"><div className="sc-stat-icon blue"><i className="bx bx-check-double"></i></div><div className="sc-stat-value">{FormatMoney("KES", billingSummary?.completed_amount)}</div><div className="sc-stat-label">Collected</div></div>
            </div>
            <div className="sc-panel">
                <div className="sc-panel-head"><div><h3>Live billing ledger</h3><p>Financial visibility across the collaborations currently delivering care.</p></div><span className="cw-count-pill">{visibleBillings.length} records</span></div>
                <div className="sc-panel-body">
                    {visibleBillings.length === 0 ? <div className="sc-empty"><i className="bx bx-receipt"></i><h3>No billing records yet</h3><p>Billing will appear as collaborations begin recording value and invoices.</p></div> : (
                        <div className="sc-list-stack">
                            {visibleBillings.map((item) => (
                                <div className="sc-list-item" key={item.billing_id}>
                                    <div>
                                        <strong>{item.collaboration_title}</strong>
                                        <div>{MaskPatientName(item.patient_name, item.payment_status)} · {item.tasks_count} tasks · {item.completed_tasks_count} completed</div>
                                    </div>
                                    <div className="cw-money-block">
                                        <strong>{FormatMoney(item.currency, item.total_amount)}</strong>
                                        <span>{item.invoice_number || item.payment_status}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </>
    );

    const renderReports = () => (
        <div className="cw-overview-grid">
            <div className="sc-panel">
                <div className="sc-panel-head"><div><h3>Collaboration evidence</h3><p>Delivery outputs, signed reports, and patient-facing evidence tied to the collaboration lifecycle.</p></div><span className="cw-count-pill">{visibleReports.length} reports</span></div>
                <div className="sc-panel-body">
                    {visibleReports.length === 0 ? <div className="sc-empty"><i className="bx bx-file"></i><h3>No reports available</h3><p>Reports generated during collaboration execution will appear here.</p></div> : (
                        <div className="cw-report-grid">
                            {visibleReports.map((item) => (
                                <div className="cw-report-card" key={item.report_id}>
                                    <div className="cw-request-head">
                                        <div>
                                            <strong>{item.title}</strong>
                                            <span>{item.collaboration.title}</span>
                                        </div>
                                        <span className={`cw-status ${item.is_signed ? "active" : "pending"}`}>{item.is_signed ? "Signed" : "Unsigned"}</span>
                                    </div>
                                    <p>{item.content_preview}</p>
                                    <div className="cw-metrics-row muted">
                                        <span>{item.task.title}</span>
                                        <span>{FormatMoney(item.task.currency, item.task.amount)}</span>
                                    </div>
                                    <div className="cw-metrics-row muted">
                                        <span>{item.generated_by}</span>
                                        <span>{FormatDate(item.generated_at)}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            <div>
                <div className="sc-panel cw-side-panel">
                    <div className="sc-panel-head"><div><h3>Report quality</h3><p>Track completion and signature readiness.</p></div></div>
                    <div className="sc-panel-body">
                        <div className="sc-list-stack">
                            <div className="sc-list-item"><strong>Total reports</strong><span>{reportsSummary?.total_reports || 0}</span></div>
                            <div className="sc-list-item"><strong>Signed reports</strong><span>{reportsSummary?.signed_reports || 0}</span></div>
                            <div className="sc-list-item"><strong>Unsigned reports</strong><span>{reportsSummary?.unsigned_reports || 0}</span></div>
                        </div>
                        <div className="cw-action-row" style={{ marginTop: 16 }}>
                            <button type="button" className="sc-btn sc-btn-primary" onClick={() => navigate("/institution/resources")}>Open resource center</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <>
            <CollaborationShell
                title={pageCopy.title}
                description={pageCopy.description}
                activeTab={activeTab}
                action={
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                        <button type="button" className="sc-btn sc-btn-outline" onClick={() => RefreshWorkspace()} disabled={loading || refreshing}>
                            <i className={`bx ${refreshing ? "bx-loader-circle bx-spin" : "bx-refresh"}`}></i>
                            Refresh
                        </button>
                        <button type="button" className="sc-btn sc-btn-primary" onClick={OpenRequestComposer}>
                            <i className="bx bx-plus"></i>
                            Create request
                        </button>
                    </div>
                }
            >
                <div className="sc-panel cw-search-panel">
                    <div className="sc-panel-body cw-search-row">
                        <div>
                            <div className="cw-eyebrow">Current facility</div>
                            <strong className="cw-clinic-name">{clinicName || "Clinic Workspace"}</strong>
                        </div>
                        <div className="sc-search cw-search-box">
                            <i className="bx bx-search"></i>
                            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search patients, requests, partners, billing, or reports" />
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="sc-panel"><div className="sc-empty"><i className="bx bx-loader-circle bx-spin"></i><h3>Loading collaboration workspace</h3><p>Preparing requests, agreements, and active care delivery workspaces.</p></div></div>
                ) : (
                    <>
                        {activeTab === "overview" && renderOverview()}
                        {activeTab === "find-requests" && renderOpportunityFeed()}
                        {activeTab === "partnership-requests" && renderPartnerOutreach()}
                        {activeTab === "received-requests" && renderAgreements()}
                        {activeTab === "accounting" && renderFinance()}
                        {activeTab === "reports" && renderReports()}
                    </>
                )}
            </CollaborationShell>

            {showCreateModal ? (
                <div className="sc-overlay" onClick={CloseRequestComposer}>
                    <div className="sc-modal sc-modal-lg" onClick={(event) => event.stopPropagation()}>
                        <div className="sc-modal-header">
                            <h2>Create collaboration request</h2>
                            <button type="button" className="sc-modal-close" onClick={CloseRequestComposer}><i className="bx bx-x"></i></button>
                        </div>
                        <div className="sc-steps">
                            {requestComposerSteps.map((item) => (
                                <div key={item.step} className={`sc-step ${requestComposerStep === item.step ? "active" : ""} ${requestComposerStep > item.step ? "done" : ""}`}>
                                    <div className="sc-step-num">{item.step}</div>
                                    <div className="sc-step-label">{item.label}</div>
                                </div>
                            ))}
                        </div>
                        <div className="sc-modal-body">
                            {renderRequestComposerBody()}
                        </div>
                        <div className="sc-modal-footer">
                            <button type="button" className="sc-btn sc-btn-ghost" onClick={CloseRequestComposer}>Cancel</button>
                            {requestComposerStep > 1 ? <button type="button" className="sc-btn sc-btn-outline" onClick={RetreatRequestComposerStep}>Back</button> : null}
                            {requestComposerStep < 4 ? <button type="button" className="sc-btn sc-btn-primary" onClick={AdvanceRequestComposerStep}>Continue</button> : null}
                            {requestComposerStep === 4 ? <button type="button" className="sc-btn sc-btn-outline" onClick={() => CreateRequest(false)} disabled={creatingRequest}>{creatingRequest ? "Saving..." : "Save draft"}</button> : null}
                            {requestComposerStep === 4 ? <button type="button" className="sc-btn sc-btn-primary" onClick={() => CreateRequest(true)} disabled={creatingRequest}>{creatingRequest ? "Publishing..." : requestForm.request_type === "open" ? "Publish publicly" : "Publish request"}</button> : null}
                        </div>
                    </div>
                </div>
            ) : null}

            {showApplicationModal && applyingToRequest ? (
                <div className="sc-overlay" onClick={CloseApplicationComposer}>
                    <div className="sc-modal sc-modal-lg" onClick={(event) => event.stopPropagation()}>
                        <div className="sc-modal-header">
                            <h2>Apply to {applyingToRequest.title}</h2>
                            <button type="button" className="sc-modal-close" onClick={CloseApplicationComposer}><i className="bx bx-x"></i></button>
                        </div>
                        <div className="sc-steps">
                            {[
                                { step: 1, label: "Qualifications" },
                                { step: 2, label: "Commercials" },
                                { step: 3, label: "Review & Submit" }
                            ].map((item) => (
                                <div key={item.step} className={`sc-step ${applicationComposerStep === item.step ? "active" : ""} ${applicationComposerStep > item.step ? "done" : ""}`}>
                                    <div className="sc-step-num">{item.step}</div>
                                    <div className="sc-step-label">{item.label}</div>
                                </div>
                            ))}
                        </div>
                        <div className="sc-modal-body">
                            <div className="cw-summary-card">
                                <strong>{applyingToRequest.creator.clinic_name || applyingToRequest.creator.name || "Partner facility"}</strong>
                                <p>{applyingToRequest.description}</p>
                                <div className="cw-metrics-row muted">
                                    <span>{MaskPatientName(applyingToRequest.patient_name, applyingToRequest.status)}</span>
                                    <span>{FormatMoney("KES", applyingToRequest.max_pay || applyingToRequest.min_pay)}</span>
                                    <span>{applyingToRequest.engagement_type.replace(/_/g, " ")}</span>
                                </div>
                            </div>
                            
                            {applicationComposerStep === 1 && (
                                <>
                                    <div className="sc-form-group"><label>Cover letter</label><textarea className="sc-textarea" rows={4} name="cover_letter" value={applicationForm.cover_letter} onChange={UpdateApplicationField} /></div>
                                    <div className="sc-form-group"><label>Qualifications</label><textarea className="sc-textarea" rows={3} name="qualifications" value={applicationForm.qualifications} onChange={UpdateApplicationField} /></div>
                                    <div className="sc-form-row">
                                        <div className="sc-form-group"><label>Certifications</label><textarea className="sc-textarea" rows={2} name="certifications" value={applicationForm.certifications} onChange={UpdateApplicationField} /></div>
                                        <div className="sc-form-group"><label>Equipment & facilities</label><textarea className="sc-textarea" rows={2} name="equipment" value={applicationForm.equipment} onChange={UpdateApplicationField} /></div>
                                    </div>
                                    <div className="sc-form-row">
                                        <div className="sc-form-group"><label>Experience (years)</label><input type="number" min="0" className="sc-input" name="experience_years" value={applicationForm.experience_years} onChange={UpdateApplicationField} /></div>
                                        <div className="sc-form-group"></div>
                                    </div>
                                </>
                            )}

                            {applicationComposerStep === 2 && (
                                <>
                                    <div className="sc-form-row">
                                        <div className="sc-form-group"><label>Proposed min rate</label><input type="number" min="0" className="sc-input" name="proposed_min_rate" value={applicationForm.proposed_min_rate} onChange={UpdateApplicationField} /></div>
                                        <div className="sc-form-group"><label>Proposed max rate</label><input type="number" min="0" className="sc-input" name="proposed_max_rate" value={applicationForm.proposed_max_rate} onChange={UpdateApplicationField} /></div>
                                    </div>
                                    <div className="sc-form-row">
                                        <div className="sc-form-group"><label>Pay mode</label><select className="sc-input" name="pay_mode" value={applicationForm.pay_mode} onChange={UpdateApplicationField}><option value="request">Per request</option><option value="person">Per person</option><option value="milestone">Per milestone</option><option value="hourly">Hourly</option></select></div>
                                        <div className="sc-form-group"><label>Patient flow capacity</label><input type="number" min="0" className="sc-input" name="patient_flow" value={applicationForm.patient_flow} onChange={UpdateApplicationField} /></div>
                                        <div className="sc-form-group"><label>Flow rate</label><select className="sc-input" name="patient_flow_rate" value={applicationForm.patient_flow_rate} onChange={UpdateApplicationField}><option value="daily">Daily</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option></select></div>
                                    </div>
                                </>
                            )}

                            {applicationComposerStep === 3 && (
                                <>
                                    <div className="sc-form-group"><label>Information request</label><textarea className="sc-textarea" rows={3} name="info_request" value={applicationForm.info_request} onChange={UpdateApplicationField} placeholder="Ask the partner for any missing information before you commit..." /></div>
                                    <label className="cw-switch-row"><input type="checkbox" name="task_acknowledgment" checked={applicationForm.task_acknowledgment} onChange={UpdateApplicationField} /><span>I confirm my facility can deliver the requested scope of work.</span></label>
                                </>
                            )}
                        </div>
                        <div className="sc-modal-footer">
                            <button type="button" className="sc-btn sc-btn-ghost" onClick={CloseApplicationComposer}>Cancel</button>
                            {applicationComposerStep > 1 && <button type="button" className="sc-btn sc-btn-outline" onClick={RetreatApplicationComposerStep}>Back</button>}
                            {applicationComposerStep < 3 && <button type="button" className="sc-btn sc-btn-primary" onClick={AdvanceApplicationComposerStep}>Continue</button>}
                            {applicationComposerStep === 3 && <button type="button" className="sc-btn sc-btn-primary" onClick={SubmitApplication} disabled={submittingApplication}>{submittingApplication ? "Submitting..." : "Submit application"}</button>}
                        </div>
                    </div>
                </div>
            ) : null}
        </>
    );
};

export default CollaborationCareWorkspacePage;
